import { InstrumentIcon } from './InstrumentIcon';

const CHART =
  'M0,150 L30,140 L60,146 L90,128 L120,132 L150,110 L180,118 L210,96 L240,104 L270,86 L300,92 L330,70 L360,80 L390,62 L420,72 L450,50 L480,58 L510,40 L540,46 L570,28 L600,22';

const FEATURES: { icon: React.ReactNode; text: string }[] = [
  { icon: <><circle cx="12" cy="12" r="9" /><path d="M12 3v18M3 12h18" /></>, text: 'Crypto, commodities & stocks in one account' },
  { icon: <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />, text: 'One USDT account for everything — trade in $ or lots' },
  { icon: <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />, text: 'Real-time prices, instant execution' },
  { icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />, text: 'Built-in stop-loss & take-profit' },
];

const CHIPS: { label: string; tag?: string }[] = [
  { label: 'Bitcoin' },
  { label: 'Ethereum' },
  { label: 'Gold', tag: 'XAU' },
  { label: 'Oil' },
  { label: 'US Stocks' },
  { label: 'Leverage', tag: 'up to 100x' },
];

const glass =
  'absolute rounded-2xl border border-white/12 bg-white/8 px-4 py-3.5 text-[13px] shadow-[0_20px_40px_rgba(0,0,0,.35)] backdrop-blur-xl';

/** Right-hand marketing panel on the login page. The product mock is decorative; its figures are illustrative. */
export function LoginShowcase() {
  return (
    <aside className="hidden py-6 pr-6 lg:block">
      <div className="relative flex h-full min-h-[640px] flex-col overflow-hidden rounded-[28px] bg-[radial-gradient(900px_500px_at_100%_0%,rgba(59,91,253,.45),transparent_60%),linear-gradient(160deg,#05060a_30%,#0b1240_100%)] p-[clamp(28px,4vw,52px)] text-white">
        <h2 className="max-w-[520px] font-serif text-[clamp(36px,3.6vw,54px)] leading-[1.05]">
          Trade for those who take it seriously
        </h2>

        <ul className="mt-7 grid gap-3">
          {FEATURES.map((f) => (
            <li key={f.text} className="flex items-center gap-3 text-[15px] text-white/88">
              <span className="grid size-[26px] place-items-center rounded-[7px] bg-white/10">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2">
                  {f.icon}
                </svg>
              </span>
              {f.text}
            </li>
          ))}
        </ul>

        <div className="mt-6 flex max-w-[560px] flex-wrap gap-2">
          {CHIPS.map((c) => (
            <span
              key={c.label}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/6 bg-white/8 px-[11px] py-[7px] text-[13.5px]"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" className="opacity-80">
                <path d="M20 6 9 17l-5-5" />
              </svg>
              {c.label}
              {c.tag && <span className="rounded-full bg-[#3b5bfd] px-[7px] py-0.5 text-[11px] font-semibold">{c.tag}</span>}
            </span>
          ))}
        </div>

        <div className="relative mt-9 min-h-[260px] flex-1" aria-hidden="true">
          <div className="absolute inset-y-0 left-0 -right-20 origin-top-left rounded-[18px] border border-white/8 bg-[rgba(14,16,24,.85)] px-5 py-[18px] shadow-[0_30px_80px_rgba(0,0,0,.5)] [transform:perspective(1400px)_rotateX(10deg)_rotateY(-10deg)_rotateZ(-3deg)]">
            <div className="flex items-center gap-3.5">
              <span className="flex items-center gap-2 text-[15px] font-semibold">
                <InstrumentIcon symbol="BTC" size={22} />
                BTC/USDT
              </span>
              <span className="text-[22px] font-semibold tabular-nums">$67,482.10</span>
              <span className="rounded-md bg-[#1fc7a0]/12 px-2 py-[3px] text-[13px] font-semibold text-[#1fc7a0]">+2.84%</span>
              <div className="ml-auto flex gap-1 text-xs text-white/50">
                {['1H', '4H', '1D', '1W'].map((tf) => (
                  <span key={tf} className={`rounded-md px-2 py-1 ${tf === '4H' ? 'bg-white/10 text-white' : ''}`}>
                    {tf}
                  </span>
                ))}
              </div>
            </div>
            <svg className="mt-2.5 block h-[190px] w-full" viewBox="0 0 600 190" preserveAspectRatio="none">
              <defs>
                <linearGradient id="showcase-fill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="#3b5bfd" stopOpacity=".45" />
                  <stop offset="1" stopColor="#3b5bfd" stopOpacity="0" />
                </linearGradient>
              </defs>
              <g stroke="rgba(255,255,255,.06)">
                <line x1="0" y1="40" x2="600" y2="40" />
                <line x1="0" y1="95" x2="600" y2="95" />
                <line x1="0" y1="150" x2="600" y2="150" />
              </g>
              <path d={`${CHART} L600,190 L0,190 Z`} fill="url(#showcase-fill)" />
              <path d={CHART} fill="none" stroke="#6d86ff" strokeWidth="2.5" />
              <circle cx="600" cy="22" r="5" fill="#fff" />
            </svg>
          </div>

          <div className={`${glass} -top-1.5 right-[6%] min-w-[190px] rotate-3`}>
            <div className="mb-1 text-[11.5px] text-white/55">Portfolio value</div>
            <div className="text-lg font-semibold tabular-nums">$24,318.55</div>
            <div className="mt-0.5 text-xs text-[#1fc7a0]">▲ +$1,204.32 today</div>
          </div>

          <div className={`${glass} bottom-[8%] left-[8%] min-w-[190px] -rotate-2`}>
            <div className="mb-1 text-[11.5px] text-white/55">Open position</div>
            <div className="mt-1.5 flex justify-between gap-[18px]">
              <span className="flex items-center gap-1.5">
                <InstrumentIcon symbol="GOLD" size={18} />
                GOLD
                <span className="ml-1.5 rounded bg-[#1fc7a0]/18 px-1.5 py-0.5 text-[10.5px] font-bold text-[#1fc7a0]">BUY 20x</span>
              </span>
              <span className="text-[#1fc7a0]">+4.21%</span>
            </div>
            <div className="mt-1.5 flex justify-between gap-[18px] text-xs text-white/55">
              <span>Entry $2,341.10</span>
              <span>PnL +$187.40</span>
            </div>
          </div>
        </div>

        <p className="mt-6 max-w-[620px] text-[11.5px] leading-[1.55] text-white/50">
          <b className="font-semibold text-white/75">Risk warning:</b> CFDs are complex instruments and come with a high
          risk of losing money rapidly due to leverage. You should consider whether you understand how CFDs work and
          whether you can afford to take the high risk of losing your money. Figures shown are illustrative.
        </p>
      </div>
    </aside>
  );
}
